import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, List, ListItem, ListItemText, Divider, Chip, Stack, TextField } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { apiService } from '../../services/apiService';

interface NomineePerson {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
  ordinal?: number;
}

interface NomineeGroup {
  ordinal: number;
  introText?: string;
  organization?: string;
  positionTitle?: string;
  nomineeCount?: number;
  url?: string;
  people?: NomineePerson[];
}

const NominationDetail: React.FC = () => {
  const { congress, nominationNumber } = useParams<{ congress: string; nominationNumber: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [nomination, setNomination] = useState<any>(null);
  const [actions, setActions] = useState<any[]>([]);
  const [committees, setCommittees] = useState<any[]>([]);
  const [hearings, setHearings] = useState<any[]>([]);
  const [nomineeGroups, setNomineeGroups] = useState<NomineeGroup[]>([]);
  // Notes (nomination)
  const [noteText, setNoteText] = useState<string>('');
  const [noteSaved, setNoteSaved] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (!congress || !nominationNumber) return;
        setError(null);
        setLoading(true);
        // Fetch core resources in parallel
        const [det, acts, coms, hrs] = await Promise.all([
          apiService.getNominationDetail(Number(congress), nominationNumber),
          apiService.getNominationActions(Number(congress), nominationNumber),
          apiService.getNominationCommittees(Number(congress), nominationNumber),
          apiService.getNominationHearings(Number(congress), nominationNumber),
        ]);

        const detail = det.data;
        setNomination(detail);
        const a = acts.data?.actions || [];
        setActions(Array.isArray(a) ? a : []);
        const c = coms.data?.committees || [];
        setCommittees(Array.isArray(c) ? c : []);
        const h = hrs.data?.hearings || [];
        setHearings(Array.isArray(h) ? h : []);

        // Build nominee groups and fetch their people
        const src = detail?.nomination || detail || {};
        const groupsSrc = Array.isArray(src?.nominees) ? src.nominees : [];
        const groups: NomineeGroup[] = groupsSrc
          .map((g: any) => ({
            ordinal: g.ordinal,
            introText: g.introText,
            organization: g.organization,
            positionTitle: g.positionTitle,
            nomineeCount: g.nomineeCount,
            url: g.url,
          }))
          .filter((g: any) => g && g.ordinal != null);

        // Fetch nominees per ordinal (limit to reasonable concurrency)
        const fetched = await Promise.all(groups.map(async (g) => {
          try {
            const resp = await apiService.getNominationOrdinalNominees(Number(congress), nominationNumber, g.ordinal);
            const people = Array.isArray(resp.data?.nominees) ? resp.data.nominees : [];
            return { ...g, people } as NomineeGroup;
          } catch {
            return { ...g, people: [] } as NomineeGroup;
          }
        }));
        setNomineeGroups(fetched);
      } catch (e: any) {
        console.error('Failed to load nomination', e);
        setError('Failed to load nomination details');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [congress, nominationNumber]);

  if (loading) return <LoadingSpinner message="Loading nomination..." />;
  if (!nomination) return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/nominations')}>Back to Nominations</Button>
      <Typography variant="h6" sx={{ mt: 2 }}>{error || 'Nomination not found'}</Typography>
    </Box>
  );

  const src = nomination.nomination || nomination;
  const cong = src.congress || src.nominationCongress || congress;
  const number = src.number || src.nominationNumber || nominationNumber;

  const latestActionText = src.latestAction?.text;
  const receivedDate = src.receivedDate;
  const citation = src.citation;
  const partNumber = src.partNumber;

  const onSaveNote = async () => {
    setNoteError(null); setNoteSaved(null);
    try {
      await apiService.createNoteForTarget({ target_type: 'nomination', target_ref: `${cong}/${number}`, text: noteText.trim() });
      setNoteSaved('Saved');
      setNoteText('');
    } catch (e: any) {
      setNoteError(e?.response?.data?.detail || 'Failed to save note');
    }
  };
  const isMilitary = src.nominationType?.isMilitary;

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/nominations')} sx={{ mb: 2 }}>
        Back to Nominations
      </Button>

      {/* Notes */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>My Notes</Typography>
        {noteSaved && <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>{noteSaved}</Typography>}
        {noteError && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{noteError}</Typography>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
          <TextField
            label="Add a note to this nomination"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <Button variant="contained" onClick={onSaveNote} disabled={!noteText.trim()}>
            Save Note
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          Nomination {number} (Congress {cong})
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          {citation && <Chip label={citation} />}
          {partNumber && <Chip label={`Part ${partNumber}`} variant="outlined" />}
          {isMilitary != null && <Chip label={isMilitary ? 'Military' : 'Civilian'} color={isMilitary ? 'secondary' : 'default'} />}
        </Stack>
        {src.description && <Typography paragraph>{src.description}</Typography>}
        {src.sourceUrl && <Button component="a" href={src.sourceUrl} target="_blank" rel="noopener noreferrer" sx={{ mb: 1 }}>Find on Congress.gov</Button>}
        {src.detailsSynced === false && <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Additional nominee and committee records have not been synchronized. See Congress.gov for the complete record.</Typography>}
        {src.organization && (
          <Typography variant="body2" color="textSecondary" paragraph>
            Organization: {src.organization}
          </Typography>
        )}
        {receivedDate && (
          <Typography variant="body2" color="textSecondary">
            Received: {new Date(`${receivedDate.slice(0, 10)}T12:00:00`).toLocaleDateString()}
          </Typography>
        )}
        {latestActionText && (
          <Typography variant="body2" color="textSecondary">
            Latest Action: {latestActionText}
          </Typography>
        )}
      </Paper>

      {/* Nominees Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Nominees</Typography>
        {nomineeGroups.length === 0 ? (
          <Typography variant="body2" color="textSecondary">{src.detailsSynced === false ? 'Nominee groups have not been synchronized.' : 'No nominee information available.'}</Typography>
        ) : (
          <Box>
            {nomineeGroups.map((g, idx) => (
              <Box key={idx} sx={{ mb: 2 }}>
                {g.introText && (
                  <Typography variant="subtitle1" gutterBottom>{g.introText}</Typography>
                )}
                <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  {g.organization && <Chip label={g.organization} />}
                  {g.positionTitle && <Chip label={g.positionTitle} variant="outlined" />}
                  {typeof g.nomineeCount === 'number' && <Chip label={`Count: ${g.nomineeCount}`} />}
                </Stack>
                {Array.isArray(g.people) && g.people.length > 0 ? (
                  <List>
                    {g.people
                      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0))
                      .map((p, i) => {
                        const name = [p.firstName, p.middleName, p.lastName, p.suffix].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
                        return (
                          <ListItem key={i} divider>
                            <ListItemText primary={name || 'Nominee'} secondary={p.ordinal ? `Ordinal ${p.ordinal}` : undefined} />
                          </ListItem>
                        );
                      })}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">No individuals listed for this group.</Typography>
                )}
                {idx < nomineeGroups.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Committees Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Committees</Typography>
        {committees.length === 0 ? (
          <Typography variant="body2" color="textSecondary">Committee records are not available locally.</Typography>
        ) : (
          <List>
            {committees.map((c: any, idx: number) => (
              <ListItem key={idx} divider>
                <ListItemText
                  primary={`${c.name || 'Committee'}${c.chamber ? ` (${c.chamber})` : ''}`}
                  secondary={Array.isArray(c.activities) && c.activities.length > 0 ?
                    c.activities.map((a: any) => `${a.date ? new Date(a.date).toLocaleString() : ''} ${a.name || ''}`.trim()).join(' • ')
                    : undefined}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Hearings Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Hearings</Typography>
        {hearings.length === 0 ? (
          <Typography variant="body2" color="textSecondary">Hearing records are not available locally.</Typography>
        ) : (
          <List>
            {hearings.map((h: any, idx: number) => (
              <ListItem key={idx} divider>
                <ListItemText
                  primary={h.citation || `Hearing ${h.number || ''}`}
                  secondary={[h.chamber, h.date ? new Date(h.date).toLocaleDateString() : null, h.jacketNumber ? `Jacket ${h.jacketNumber}` : null]
                    .filter(Boolean)
                    .join(' • ')}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Actions Section */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Actions</Typography>
        {actions.length === 0 ? (
          <Typography variant="body2" color="textSecondary">Full action history is not available locally; the latest action appears above.</Typography>
        ) : (
          <List>
            {actions.map((a: any, idx: number) => (
              <ListItem key={idx} divider>
                <ListItemText
                  primary={a.text || 'Action'}
                  secondary={a.actionDate ? new Date(a.actionDate).toLocaleDateString() : ''}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default NominationDetail;
